import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { jobApplications } from '../services/apiService';
import { Loader } from '../components/UI/LoadingSpinner';
import ContactsTab from '../components/JobApplication/ContactsTab';
import CommunicationsTab from '../components/JobApplication/CommunicationsTab';
import ImportantDatesTab from '../components/JobApplication/ImportantDatesTab';
import EventsTab from '../components/JobApplication/EventsTab';
import NotesTab from '../components/JobApplication/NotesTab';
import { ArrowLeft, FileText, Phone, MessageSquare, User, MapPin, Calendar, Briefcase, Edit, Trash2, CalendarDays, Clock, StickyNote, Link2 as LinkIcon } from 'lucide-react';
import Avatar from '../components/UI/Avatar';
import TalentFormModal from '../components/UI/TalentFormModal';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import { getTalentTypeLabel, getTalentTypeIcon, getTalentTypeHexColor } from '../utils/talentTypeUtils';
import toast from 'react-hot-toast';
import { getToolIcon } from '../utils/toolIconUtils.jsx';

const JobApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Тип таланта, в который переводим заявку; null — модалка закрыта.
  // Те же действия, что в колонке Actions таблицы заявок
  const [convertType, setConvertType] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await jobApplications.getJobApplicationByIdWithRelations(id);
      
      if (response.success) {
        // Обрабатываем новую структуру ответа: response.data.data
        const applicationData = response.data?.data || response.data;
        setApplication(applicationData);
      } else {
        setError('Failed to fetch application');
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Error loading application');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await jobApplications.deleteJobApplication(id);
      navigate('/job-applications');
    } catch (err) {
      console.error('Error deleting application:', err);
      toast.error('Error deleting application');
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-xs">No Status</span>;
    
    return (
      <span 
        className="px-2 py-1 rounded text-xs text-white"
        style={{ backgroundColor: status.color || '#6B7280' }}
      >
        {status.name}
      </span>
    );
  };


  if (loading) {
    return <Loader variant="component" size="md" />;
  }

  if (error || !application) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error || 'Application not found'}
        </div>
        <Link to="/job-applications" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mt-4 inline-block">
          ← Back to Applications
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FileText },
    { id: 'contacts', name: 'Contacts', icon: Phone },
    { id: 'communications', name: 'Communications', icon: MessageSquare },
    { id: 'important-dates', name: 'Important Dates', icon: CalendarDays },
    { id: 'notes', name: 'Notes', icon: StickyNote },
    { id: 'history', name: 'History', icon: Clock }
  ];

  return (
    <div className="bg-light-bg-primary dark:bg-dark-bg-primary">
      <title>{application?.name ? `${application.name} | Talent Service` : 'Talent Service'}</title>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <Link 
            to="/job-applications" 
            className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Applications</span>
          </Link>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="mb-2 break-words text-2xl font-bold text-light-text-primary dark:text-dark-text-primary sm:text-3xl">
              {application.name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Created: {formatDate(application.created_at)}</span>
              </div>
              {application.createdByUser && (
                <div className="flex items-center space-x-2">
                  <Avatar
                    avatar={application.createdByUser.avatar}
                    name={application.createdByUser.fullName || application.createdByUser.name}
                    email={application.createdByUser.email}
                    size="xs"
                  />
                  <span>by {application.createdByUser.fullName || application.createdByUser.name || application.createdByUser.email}</span>
                </div>
              )}
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Updated: {formatDate(application.updated_at)}</span>
              </div>
              {application.updatedByUser && (
                <div className="flex items-center space-x-2">
                  <Avatar
                    avatar={application.updatedByUser.avatar}
                    name={application.updatedByUser.fullName || application.updatedByUser.name}
                    email={application.updatedByUser.email}
                    size="xs"
                  />
                  <span>by {application.updatedByUser.fullName || application.updatedByUser.name || application.updatedByUser.email}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end">
            <Link
              to={`/job-applications/${id}/edit`}
              className="flex items-center justify-center space-x-2 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </Link>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="flex items-center justify-center space-x-2 bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 min-w-0 overflow-x-auto border-b border-light-border-primary scrollbar-hide dark:border-dark-border-primary">
        <nav className="-mb-px flex min-w-max snap-x snap-proximity flex-nowrap gap-6 sm:gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex shrink-0 snap-start items-center gap-2 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary hover:border-light-border-primary dark:hover:border-dark-border-primary'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow border border-light-border-primary dark:border-dark-border-primary">
        {activeTab === 'overview' && (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Basic Information</span>
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">ID</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary font-mono">{application.id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Name</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">{application.name}</dd>
                  </div>
                  {/* Талант, в которого переведена заявка, — или те же действия
                      перевода, что в колонке Actions таблицы заявок. Обратная
                      ссылка — карточка Job Application в Basic Info таланта */}
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Talent</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.talent ? (
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/talents/${application.talent.id}`}
                            className="font-medium text-primary-600 dark:text-primary-400 visited:text-purple-700 dark:visited:text-purple-400 hover:underline"
                            title={`Open talent profile #${application.talent.id}`}
                          >
                            {`${application.talent.last_name || ''} ${application.talent.first_name || ''}`.trim() || `#${application.talent.id}`}
                          </Link>
                          {(() => {
                            // Тот же чип типа, что в шапке профиля таланта
                            const TypeIcon = getTalentTypeIcon(application.talent.talent_type);
                            return (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md text-white shrink-0"
                                style={{ backgroundColor: getTalentTypeHexColor(application.talent.talent_type) }}
                              >
                                <TypeIcon className="h-3 w-3" />
                                {getTalentTypeLabel(application.talent.talent_type)}
                              </span>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-light-text-tertiary dark:text-dark-text-tertiary mr-1">Not converted yet</span>
                          <button
                            onClick={() => setConvertType('candidate')}
                            className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                            title="Convert to Candidate"
                          >
                            → Candidate
                          </button>
                          <button
                            onClick={() => setConvertType('employee')}
                            className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                            title="Convert to Employee"
                          >
                            → Employee
                          </button>
                          <button
                            onClick={() => setConvertType('presale')}
                            className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                            title="Convert to Presale"
                          >
                            → Presale
                          </button>
                        </div>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Gender</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary capitalize">{application.gender || 'Not specified'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Age</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">{application.age || 'Not specified'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Birthday</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.birthday ? formatDate(application.birthday) : 'Not specified'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Status and Management */}
              <div>
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center space-x-2">
                  <Briefcase className="w-5 h-5" />
                  <span>Status & Management</span>
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Status</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">{getStatusBadge(application.status)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Manager</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.manager ? (
                        <div className="flex items-center space-x-2">
                          <Avatar
                            avatar={application.manager.avatar}
                            name={application.manager.name}
                            email={application.manager.email}
                            size="sm"
                            showInitialOnMissing
                            initialBgClassName="bg-light-bg-tertiary dark:bg-dark-bg-tertiary"
                            iconBgClassName="bg-light-bg-tertiary dark:bg-dark-bg-tertiary"
                            initialTextClassName="text-light-text-secondary dark:text-dark-text-secondary font-medium"
                            iconClassName="text-light-text-secondary dark:text-dark-text-secondary"
                          />
                          <span>{application.manager.name}</span>
                        </div>
                      ) : (
                        'Not assigned'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Country</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.country ? (
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                          <span>{application.country.name}</span>
                        </div>
                      ) : (
                        'Not specified'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">City</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.city ? (
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                          <span>{application.city.name}</span>
                        </div>
                      ) : (
                        'Not specified'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-light-text-tertiary dark:text-dark-text-tertiary">Job Post</dt>
                    <dd className="text-sm text-light-text-primary dark:text-dark-text-primary">
                      {application.job_post ? (
                        <div className="flex items-center space-x-2">
                          <Briefcase className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
                          <span>{application.job_post.name}</span>
                        </div>
                      ) : application.job_post_id ? (
                        <span className="italic text-light-text-tertiary dark:text-dark-text-tertiary">[Deleted Post]</span>
                      ) : (
                        'Not linked'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Professions */}
            {application.professions && application.professions.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center space-x-2">
                  <Briefcase className="w-5 h-5" />
                  <span>Professions</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {application.professions.map((profession) => (
                    <div key={profession.id} className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                      <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                        {profession.profession?.name || profession.profession_name || `Profession ID: ${profession.profession_id}`}
                      </div>
                      {(profession.department?.name || profession.department_name) && (
                        <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
                          Department: {profession.department?.name || profession.department_name}
                        </div>
                      )}
                      <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mt-2">
                        Assigned: {profession.assigned_at ? formatDate(profession.assigned_at) : 'Unknown'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {application.links && application.links.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4 flex items-center space-x-2">
                  <LinkIcon className="w-5 h-5" />
                  <span>Links</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {application.links.map((link) => (
                    <div key={link.id} className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                        <span className="inline-flex items-center flex-shrink-0" title={link.tool_name || link.tool?.name || ''}>
                          {link.tool
                            ? getToolIcon(link.tool, { size: 'h-5 w-5' })
                            : <LinkIcon className="h-5 w-5" />}
                        </span>
                        <span>{link.tool_name || link.tool?.name || `Tool ID: ${link.tool_id}`}</span>
                      </div>
                      <a
                        href={link.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline break-all mt-1 inline-block"
                      >
                        {link.value}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {application.contacts?.length || 0}
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-300">Contacts</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {application.communications?.length || 0}
                </div>
                <div className="text-sm text-green-800 dark:text-green-300">Communications</div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {application.professions?.length || 0}
                </div>
                <div className="text-sm text-purple-800 dark:text-purple-300">Professions</div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {application.notes?.length || 0}
                </div>
                <div className="text-sm text-indigo-800 dark:text-indigo-300">Notes</div>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {application.status ? 'Active' : 'No Status'}
                </div>
                <div className="text-sm text-orange-800 dark:text-orange-300">Status</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <ContactsTab applicationId={id} />
        )}

        {activeTab === 'communications' && (
          <CommunicationsTab applicationId={id} />
        )}

        {activeTab === 'important-dates' && (
          <ImportantDatesTab applicationId={id} />
        )}

        {activeTab === 'notes' && (
          <NotesTab applicationId={id} />
        )}

        {activeTab === 'history' && (
          <EventsTab applicationId={id} />
        )}
      </div>

      {/* Перевод в талант — тот же TalentFormModal и те же initialData, что в таблице заявок */}
      <TalentFormModal
        isOpen={convertType !== null}
        onClose={() => setConvertType(null)}
        onSuccess={() => {
          toast.success(`Job Application successfully converted to ${convertType || 'talent'}!`, {
            duration: 3000,
            position: 'bottom-right',
          });
          // Модалка закроется сама (TalentFormModal зовёт onClose после onSuccess).
          // Перечитываем заявку: вместо кнопок перевода появится ссылка на талант
          fetchApplication();
        }}
        initialData={{
          job_application_id: application.id,
          manager_id: application.manager_id || '',
          last_name: application.name?.split(' ')[0] || '',
          first_name: application.name?.split(' ').slice(1).join(' ') || '',
          country_id: application.country_id || '',
          city_id: application.city_id || '',
          talent_type: convertType || 'candidate',
          public_visibility: false,
          public_profile_active: false,
          professions: application.professions || [],
          contacts: application.contacts || [],
          languages: application.languages || [],
          cv: [],
          media: []
        }}
        jobApplicationInfo={convertType ? { ...application, talentType: convertType } : null}
        title="Convert Job Application to Talent"
        submitText="Convert"
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Job Application"
        message="Are you sure you want to delete this application?"
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default JobApplicationDetail;
